import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Table, Button, Modal, Badge, Form } from "react-bootstrap";
import MessageModal from "../Pages/MessageModel";
import { imgUrl } from "src/actions/types";
import { getAllDoctors } from "../../actions/doctorActions";
import { listBlogs, updateBlog, createBlog } from "src/actions/blogActions";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { Link } from 'react-router-dom'; 
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
const BLOG_DEFAULT_IMAGE = "/assets/img/blog_default.jpg";

const urlToFile = async (url, filename, mimeType) => {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new File([buf], filename, { type: mimeType });
};
const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatShortText = (str, length = 50) => {
  if (!str) return "-";
  return str.length > length ? str.substring(0, length) + "..." : str;
};

const AdminDoctorPage = ({
  doctors,
  blogs,
  loading,
  error,
  getAllDoctors,
  listBlogs,
  updateBlog,
  createBlog,
}) => {
  const [localBlogs, setLocalBlogs] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    title: "",
    message: "",
    variant: "",
  });
  const [viewBlog, setViewBlog] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  // Add blog modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBlogForm, setNewBlogForm] = useState({
    title: "",
    content: "",
    image: null,
  });
  const [newBlogImagePreview, setNewBlogImagePreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    status: "All",
    sortBy: "date_desc",
  });

  useEffect(() => {
    getAllDoctors();
    listBlogs();
  }, [getAllDoctors, listBlogs]);

  // Combine and enrich blog data with doctor info
  useEffect(() => {
    if (!loading && Array.isArray(doctors) && Array.isArray(blogs)) {
      const merged = blogs.map((blog) => {
        const doctor = doctors.find((d) => d.hospitalID === blog.hospitalID);

        let isAdminBlog =
          !blog.hospitalID ||
          blog.hospitalID === 0 ||
          (blog.createdBy && blog.createdBy === "admin");
        // Update as per your real backend admin marker: adjust field and value as appropriate

        return {
          ...blog,
          doctorName: doctor
            ? `Dr. ${doctor.firstName} ${doctor.lastName}`
            : isAdminBlog
            ? "Admin"
            : "Unknown",
          doctorImg: doctor
            ? doctor.uploadPhoto
              ? `${imgUrl}${doctor.uploadPhoto}`
              : "/assets/img/doctors/doctor-thumb-01.jpg"
            : isAdminBlog
            ? "/assets/img/doctors/doctor-thumb-01.jpg" // Provide a real admin avatar path
            : "/assets/img/doctors/doctor-thumb-01.jpg",
          doctorMobile: doctor
            ? doctor.phoneNumber
            : isAdminBlog
            ? "admin@email.com"
            : "-",
          doctorSpeciality: doctor
            ? doctor.Specializations?.map((s) => s.Specialization).join(", ") ||
              "-"
            : isAdminBlog
            ? "Admin"
            : "-",
        };
      });
      setLocalBlogs(merged);
    }
  }, [doctors, blogs, loading]);

  // Filtering and sorting
  const filteredBlogs = useMemo(() => {
    let filtered = [...localBlogs];

    if (filters.status !== "All") {
      filtered = filtered.filter((blog) => blog.status === filters.status);
    }
    if (filters.search.trim() !== "") {
      const lowerSearch = filters.search.toLowerCase();
      filtered = filtered.filter(
        (blog) =>
          blog.title?.toLowerCase().includes(lowerSearch) ||
          blog.content?.toLowerCase().includes(lowerSearch)
      );
    }

    switch (filters.sortBy) {
      case "date_asc":
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "date_desc":
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "title_asc":
        filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
      case "title_desc":
        filtered.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
        break;
      default:
        break;
    }
    return filtered;
  }, [localBlogs, filters]);

  const handleToggleStatus = async (blog, index) => {
    const newStatus = blog.status === "published" ? "draft" : "published";

    setStatusUpdatingId(blog.id);
    const updatedBlogs = [...localBlogs];
    updatedBlogs[index].status = newStatus;
    setLocalBlogs(updatedBlogs);

    const formData = new FormData();
    formData.append("id", blog.id);
    formData.append("title", blog.title || "");
    formData.append("content", blog.content || "");
    formData.append("status", newStatus);
    formData.append("hospitalID", blog.hospitalID);
    formData.append("createdAt", blog.createdAt);

    if (blog.image) {
      formData.append("image", blog.image);
    }

    try {
      await updateBlog(formData);
      setModal({
        show: true,
        title: "Success",
        message: "Blog status updated successfully",
        variant: "success",
      });
      await listBlogs();
    } catch (error) {
      setModal({
        show: true,
        title: "Error",
        message: `Failed to update status: ${error.message || ""}`,
        variant: "danger",
      });
      // rollback
      updatedBlogs[index].status = blog.status;
      setLocalBlogs(updatedBlogs);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // New blog form handlers
  const handleNewBlogInput = (e) => {
    if (e.target.name === "image" && e.target.files.length > 0) {
      setNewBlogForm((prev) => ({ ...prev, image: e.target.files[0] }));
      setNewBlogImagePreview(URL.createObjectURL(e.target.files[0]));
    } else {
      setNewBlogForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

 const handleNewBlogSubmit = async (e) => {
  e.preventDefault();

  if (!newBlogForm.title.trim() || !newBlogForm.content.trim()) {
    setModal({
      show: true,
      title: "Validation Error",
      message: "Title and Content are required",
      variant: "warning",
    });
    return;
  }

  setIsCreating(true);

  const formData = new FormData();
  formData.append("title", newBlogForm.title);
  formData.append("content", newBlogForm.content);
  formData.append("status", "created");

  // --- Binary Logic Start ---
  if (newBlogForm.image) {
    // Agar user ne image upload ki hai
    formData.append("image", newBlogForm.image);
  } else {
    try {
      // Agar image upload nahi ki, toh default image ko binary banao
      const defaultFile = await urlToFile(
        BLOG_DEFAULT_IMAGE,
         'blog_default.jpg', // File ka naam
         'image/jpeg'  
      );
      formData.append("image", defaultFile);
    } catch (err) {
      console.error("Default image fetch error:", err);
    }
  }
  // --- Binary Logic End ---

  const hospitalId = localStorage.getItem("hospitalCode");
  if (hospitalId) formData.append("hospitalID", hospitalId);

  try {
    await createBlog(formData);
    setModal({
      show: true,
      title: "Success",
      message: "Blog created successfully",
      variant: "success",
    });
    setShowAddModal(false);
    setNewBlogForm({ title: "", content: "", image: null });
    setNewBlogImagePreview(null);
    await listBlogs();
  } catch (error) {
    setModal({
      show: true,
      title: "Error",
      message: `Blog creation failed: ${error.message || error}`,
      variant: "danger",
    });
  } finally {
    setIsCreating(false);
  }
};

  return (
    <>
      <MessageModal
        {...modal}
        onClose={() => setModal({ ...modal, show: false })}
      />

      <div className="page-wrapper ml-0">
            <div className="content-container">

     
         <div className="page-header filter-bar rounded">
                          <div className="row align-items-center">
                            <div className="col">
                              <h4 className="page-title">All Doctor Blogs</h4>
                              <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                  <Link to="/doctor/dashboard">Admin Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Blogs</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                

        {/* Filters */}
        <div className="d-flex gap-2 flex-wrap mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search Title or Content"
            style={{ maxWidth: 300 }}
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
          />
          <select
            className="form-select"
            style={{ maxWidth: 150 }}
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
          >
            <option value="All">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="created">Created</option>
            <option value="Edited">Edited</option>
          </select>
          <select
            className="form-select"
            style={{ maxWidth: 150 }}
            value={filters.sortBy}
            onChange={(e) =>
              setFilters((f) => ({ ...f, sortBy: e.target.value }))
            }
          >
            <option value="date_desc">Date Desc</option>
            <option value="date_asc">Date Asc</option>
            <option value="title_asc">Title Asc</option>
            <option value="title_desc">Title Desc</option>
          </select>

          {/* Add New Blog Button */}
          <button className="btn btn-info rounded-pill my-1" onClick={() => setShowAddModal(true)}>
            Add New Blog
          </button>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center my-5">
            <div className="spinner-border"></div>
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center my-5 text-muted">
            <i className="fas fa-blog fa-3x mb-3"></i>
            <p>No blogs found.</p>
          </div>
        ) : (
          <div className="card">
          <div className="card-body">
          <Table hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Blog ID</th>
                <th>Doctor</th>
                <th>Title</th>
                <th>Content</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Mobile</th>
                <th>Speciality</th>
                <th>Publish</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((blog, index) => (
                <tr key={blog.id}>
                  <td>{blog.id}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={blog.doctorImg}
                        alt="Doctor"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          marginRight: 10,
                        }}
                      />
                      {blog.doctorName}
                    </div>
                  </td>
                  <td>{formatShortText(blog.title, 30)}</td>
                  <td>{formatShortText(stripHtmlTags(blog.content), 50)}</td>
                  <td>
                    <span
                      className={
                        blog.status === "published"
                          ? "publish-label"
                          : blog.status === "draft"
                          ? "draft-label"
                          : blog.status === "created"
                          ? "created-label"
                          : blog.status === "Edited"
                          ? "edited-label"
                          : ""
                      }
                    >
                      {blog.status}
                    </span>
                  </td>
                  <td>{formatDate(blog.createdAt)}</td>
                  <td>{blog.doctorMobile}</td>
                  <td>{blog.doctorSpeciality}</td>
                  <td>
                    <div>
                      <input
                        type="checkbox"
                        id={`toggle_${blog.id}`}
                        className="check"
                        checked={blog.status === "published"}
                        onChange={() => handleToggleStatus(blog, index)}
                        disabled={statusUpdatingId === blog.id}
                      />
                      <label
                        htmlFor={`toggle_${blog.id}`}
                        className="checktoggle"
                      ></label>
                    </div>
                  </td>
                  <td>
                    <button
                      className="i-btn-view"
                      size="sm"
                      onClick={() => setViewBlog(blog)}
                    >
                      
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
          </div>
        )}

        {/* Add Blog Modal */}
        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          centered
          size="lg"
        >
          <Modal.Header className='bg-info text-white ' closeButton closeVariant="white">
            <Modal.Title>Add New Blog</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleNewBlogSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={newBlogForm.title}
                  onChange={handleNewBlogInput}
                  placeholder="Enter blog title"
                  maxLength={150}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Content</Form.Label>
                <ReactQuill
                  theme="snow"
                  value={newBlogForm.content}
                  onChange={(content) =>
                    setNewBlogForm((prev) => ({ ...prev, content }))
                  }
                  placeholder="Write blog content here"
                  style={{ height: "150px", marginBottom: "40px" }}
                />
              </Form.Group>
            <Form.Group controlId="formFile" className="mb-3">
  <Form.Label>Image</Form.Label>
  <Form.Control
    type="file"
    name="image"
    accept="image/*"
    onChange={handleNewBlogInput}
  />
  
  {/* Red color note as requested */}
  <p style={{ color: 'red', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
     Always upload image in JPG or PNG format.
  </p>

  {/* Image Preview: Agar nayi image hai toh wo dikhao, warna default wali preview dikhao */}
  <img
    src={newBlogImagePreview || BLOG_DEFAULT_IMAGE}
    alt="Preview"
    style={{
      marginTop: 10,
      width: "100%",
      height: "auto",
      maxHeight: 200,
      objectFit: "cover",
      borderRadius: "8px",
      border: "1px solid #ddd"
    }}
  />
</Form.Group>
              <button className="btn btn-info rounded-pill" type="submit"  disabled={isCreating}>
                {isCreating ? "Creating..." : "Create Blog"}
              </button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Blog View Modal */}
        <Modal
          show={!!viewBlog}
          onHide={() => setViewBlog(null)}
          centered
          size="lg"
        >
          <Modal.Header className='bg-info text-white ' closeButton closeVariant="white">
            <Modal.Title >{viewBlog?.title || "Blog Details"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <img
              src={
                viewBlog?.image
                  ? `${imgUrl}${viewBlog.image}`
                  : "/assets/img/blog-placeholder.webp"
              }
              alt="Blog"
              className="img-fluid rounded mb-3"
            />
            {/* Render rich text content as HTML */}
            <div
              dangerouslySetInnerHTML={{
                __html: viewBlog?.content || "<p>No content available.</p>",
              }}
            />
            <hr />
            <p>
              <strong>Doctor:</strong> {viewBlog?.doctorName || "Unknown"}
            </p>
            <p>
              <strong>Status:</strong> {viewBlog?.status}
            </p>
            <p>
              <strong>Created At:</strong>{" "}
              {viewBlog?.createdAt
                ? new Date(viewBlog.createdAt).toLocaleString()
                : "-"}
            </p>
          </Modal.Body>
        </Modal>
      </div>
      </div>
    </>
  );
};

export default connect(
  (state) => ({
    doctors: state.doctorList?.doctors || [],
    blogs: state.blogReducer?.blogs || [],
    loading: state.doctorList?.loading || state.blogReducer?.loading,
    error: state.doctorList?.error || state.blogReducer?.error,
  }),
  {
    getAllDoctors,
    listBlogs,
    updateBlog,
    createBlog,
  }
)(AdminDoctorPage);
